import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import {
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  reload,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  type Auth,
  type User as FirebaseUser,
} from "firebase/auth";
import { ApiError, api, configureApiAuth } from "./api";

export type SessionUser = {
  uid: string;
  email: string;
  name: string;
  photoUrl?: string | null;
  emailVerified: boolean;
  role: "USER" | "ADMIN";
  isDevelopment?: boolean;
};

type AuthContextValue = {
  user: SessionUser | null;
  loading: boolean;
  firebaseConfigured: boolean;
  developmentAuthEnabled: boolean;
  signIn(email: string, password: string): Promise<void>;
  signUp(input: { name: string; email: string; password: string; acceptedTerms: boolean }): Promise<{ needsVerification: boolean }>;
  signInGoogle(acceptedTerms?: boolean): Promise<void>;
  signOutUser(): Promise<void>;
  refreshSession(): Promise<void>;
  resendVerification(): Promise<void>;
  refreshVerification(): Promise<void>;
  resetPassword(email: string): Promise<void>;
  enterDevelopmentMode(): Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const firebaseConfigured = Boolean(firebaseConfig.apiKey && firebaseConfig.authDomain && firebaseConfig.projectId && firebaseConfig.appId);
const developmentAuthEnabled = import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_BYPASS === "true";
export const emailVerificationRequired = import.meta.env.VITE_REQUIRE_EMAIL_VERIFICATION === "true";
let firebaseApp: FirebaseApp | null = null;
let firebaseAuth: Auth | null = null;

function getFirebase(): Auth | null {
  if (!firebaseConfigured || typeof window === "undefined") return null;
  if (!firebaseApp) firebaseApp = getApps()[0] || initializeApp(firebaseConfig);
  if (!firebaseAuth) firebaseAuth = getAuth(firebaseApp);
  return firebaseAuth;
}

type SyncedIdentity = Awaited<ReturnType<typeof api.syncAuth>>;

export function isRetryableAuthSyncError(error: unknown) {
  if (!(error instanceof ApiError)) return true;
  return error.status === 0 || error.status === 408 || error.status === 425 || error.status === 429 || error.status >= 500;
}

function sessionFromFirebase(user: FirebaseUser, synced?: SyncedIdentity, previous?: SessionUser | null): SessionUser {
  return {
    uid: user.uid,
    email: user.email || "",
    name: synced?.profile?.name || synced?.user.name || user.displayName || user.email?.split("@")[0] || "Criador",
    photoUrl: synced?.profile?.avatarUrl || synced?.user.avatarUrl || user.photoURL,
    emailVerified: user.emailVerified || !emailVerificationRequired,
    // A failed sync says nothing about the role. Asserting USER would quietly
    // strip an administrator of every admin entry in the interface until the
    // next successful sign-in, so the last known answer is kept instead. The
    // API still checks the role on every request, so the worst case is a menu
    // entry that leads to a refusal rather than access nobody granted.
    role: synced ? (synced.user.role === "ADMIN" ? "ADMIN" : "USER") : (previous?.uid === user.uid ? previous.role : "USER"),
  };
}

// /api/auth/sync is rate limited and can be cold. One transient failure used to
// be indistinguishable from "this account is not an administrator".
async function syncWithRetry(attempts = 3): Promise<SyncedIdentity | undefined> {
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await api.syncAuth({});
    } catch (error) {
      if (!isRetryableAuthSyncError(error) || attempt === attempts) {
        console.warn("[score-creator] não foi possível sincronizar a sessão:", error);
        return undefined;
      }
      await new Promise((resolve) => setTimeout(resolve, attempt * 700));
    }
  }
  return undefined;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const saved = localStorage.getItem("score-dev-session");
    if (saved && developmentAuthEnabled) {
      try {
        setUser(JSON.parse(saved));
        configureApiAuth(async () => null, import.meta.env.VITE_DEV_USER_ID || "dev-user");
        setLoading(false);
        return;
      } catch {
        localStorage.removeItem("score-dev-session");
      }
    }
    const auth = getFirebase();
    if (!auth) {
      setLoading(false);
      return;
    }

    return onAuthStateChanged(auth, async (nextUser) => {
      setFirebaseUser(nextUser);
      configureApiAuth(async () => nextUser?.getIdToken() || null);
      if (!nextUser) {
        setUser(null);
        setLoading(false);
        return;
      }
      const synced = await syncWithRetry();
      setUser((previous) => sessionFromFirebase(nextUser, synced, previous));
      setLoading(false);
    });
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const auth = getFirebase();
    if (!auth) throw new Error("A autenticação ainda não foi configurada neste ambiente.");
    const result = await signInWithEmailAndPassword(auth, email, password);
    setFirebaseUser(result.user);
    configureApiAuth(async () => result.user.getIdToken());
    const synced = await api.syncAuth({});
    setUser(sessionFromFirebase(result.user, synced));
  }, []);

  const signUp = useCallback(async (input: { name: string; email: string; password: string; acceptedTerms: boolean }) => {
    const auth = getFirebase();
    if (!auth) throw new Error("A autenticação ainda não foi configurada neste ambiente.");
    const result = await createUserWithEmailAndPassword(auth, input.email, input.password);
    await updateProfile(result.user, { displayName: input.name });
    setFirebaseUser(result.user);
    configureApiAuth(async () => result.user.getIdToken());
    const synced = await api.syncAuth({ acceptedTerms: input.acceptedTerms, acceptedPrivacy: input.acceptedTerms, name: input.name });
    if (emailVerificationRequired) await sendEmailVerification(result.user);
    setUser({ ...sessionFromFirebase(result.user, synced), name: input.name });
    return { needsVerification: emailVerificationRequired };
  }, []);

  const signInGoogle = useCallback(async (acceptedTerms = false) => {
    const auth = getFirebase();
    if (!auth) throw new Error("A autenticação ainda não foi configurada neste ambiente.");
    const result = await signInWithPopup(auth, new GoogleAuthProvider());
    setFirebaseUser(result.user);
    configureApiAuth(async () => result.user.getIdToken());
    const synced = await api.syncAuth({ acceptedTerms, acceptedPrivacy: acceptedTerms, name: result.user.displayName || undefined });
    setUser(sessionFromFirebase(result.user, synced));
  }, []);

  const signOutUser = useCallback(async () => {
    const auth = getFirebase();
    if (auth) await signOut(auth);
    localStorage.removeItem("score-dev-session");
    configureApiAuth(async () => null);
    setFirebaseUser(null);
    setUser(null);
  }, []);

  const refreshSession = useCallback(async () => {
    if (!firebaseUser) return;
    configureApiAuth(async () => firebaseUser.getIdToken());
    const synced = await api.syncAuth({});
    setUser(sessionFromFirebase(firebaseUser, synced));
  }, [firebaseUser]);

  const resendVerification = useCallback(async () => {
    if (!firebaseUser) throw new Error("Entre novamente para reenviar a verificação.");
    await sendEmailVerification(firebaseUser);
  }, [firebaseUser]);

  const refreshVerification = useCallback(async () => {
    if (!firebaseUser) throw new Error("Entre novamente para confirmar seu e-mail.");
    await reload(firebaseUser);
    await firebaseUser.getIdToken(true);
    if (!firebaseUser.emailVerified) throw new Error("O e-mail ainda não foi confirmado. Abra o link recebido e tente de novo.");
    configureApiAuth(async () => firebaseUser.getIdToken());
    const synced = await api.syncAuth({});
    setUser(sessionFromFirebase(firebaseUser, synced));
  }, [firebaseUser]);

  const resetPassword = useCallback(async (email: string) => {
    const auth = getFirebase();
    if (!auth) throw new Error("A autenticação ainda não foi configurada neste ambiente.");
    await sendPasswordResetEmail(auth, email);
  }, []);

  const enterDevelopmentMode = useCallback(async () => {
    if (!developmentAuthEnabled) return;
    const developmentUser: SessionUser = {
      uid: import.meta.env.VITE_DEV_USER_ID || "dev-user",
      email: "criador@local.dev",
      name: "Criador Local",
      emailVerified: true,
      role: window.location.search.includes("admin=1") ? "ADMIN" : "USER",
      isDevelopment: true,
    };
    localStorage.setItem("score-dev-session", JSON.stringify(developmentUser));
    configureApiAuth(async () => null, developmentUser.uid);
    setUser(developmentUser);
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    firebaseConfigured,
    developmentAuthEnabled,
    signIn,
    signUp,
    signInGoogle,
    signOutUser,
    refreshSession,
    resendVerification,
    refreshVerification,
    resetPassword,
    enterDevelopmentMode,
  }), [enterDevelopmentMode, loading, refreshSession, refreshVerification, resendVerification, resetPassword, signIn, signInGoogle, signOutUser, signUp, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth deve ser usado dentro de AuthProvider");
  return value;
}
